import { matchPath } from 'react-router';
import { QuickCapturePage } from '../components/routing/MobileCaptureOrRedeem';
import { isMobileSupportedPath } from '../config/mobileRoutes';
import { ROUTES } from '../constants/routes';
import {
  AccountSettings,
  AttachmentDetail,
  Dashboard,
  MobileBirdPage,
  MobileFoldersWorkspacePage,
  NoteDetail,
  ProtocolDetail,
  ProtocolLibraryGridPage,
  ProtocolRunPage,
  SpreadsheetDetail,
  WorkspaceCanvasPage,
} from '../lazyPages';
import { isPhoneViewport } from '../utils/phoneViewport';

/**
 * The open page's code, requested alongside `/auth/login` instead of after it.
 *
 * Measured on staging (2026-10-28, `e2e/scripts/boot-benchmark.ts`): the route
 * tree renders only once `/auth/login` answers, so the page's lazy chunk was
 * requested after it, adding a fetch to every signed-in load (about 200 ms on
 * a throttled phone). Called from the session-restored hook, so only a browser
 * that holds a signed-in Firebase session pays for it.
 *
 * Code only: the same public, fingerprinted files for every user. No data is
 * requested and nothing renders earlier; a wrong guess costs one download the
 * real navigation then reuses or ignores.
 */

interface PageChunk {
  preload: () => void;
}

/** Desktop pages people open first, by route pattern. Unlisted routes are
 * left to load as before. */
const DESKTOP_PAGES: ReadonlyArray<readonly [string, PageChunk]> = [
  [ROUTES.root, Dashboard],
  [ROUTES.dashboard, Dashboard],
  [ROUTES.collections, WorkspaceCanvasPage],
  [ROUTES.collection.pattern, WorkspaceCanvasPage],
  [ROUTES.note.pattern, NoteDetail],
  [ROUTES.attachment.pattern, AttachmentDetail],
  [ROUTES.spreadsheet.pattern, SpreadsheetDetail],
  [ROUTES.protocols, ProtocolLibraryGridPage],
  [ROUTES.protocol.pattern, ProtocolDetail],
  [ROUTES.protocolRun.pattern, ProtocolRunPage],
  [ROUTES.settings, AccountSettings],
];

/** The phone shell's own pages. Collections render the phone workspace page
 * there (`WorkspacePageForShell`). */
const PHONE_PAGES: ReadonlyArray<readonly [string, PageChunk]> = [
  [ROUTES.mobileCapture, QuickCapturePage],
  [ROUTES.mobileWorkspace, MobileFoldersWorkspacePage],
  [ROUTES.mobileBird, MobileBirdPage],
  [ROUTES.collections, MobileFoldersWorkspacePage],
  [ROUTES.collection.pattern, MobileFoldersWorkspacePage],
];

function pageFor(pathname: string, search: string, phone: boolean): PageChunk | undefined {
  if (phone) {
    // A token on /m/capture is a magic link, which redeems instead of capturing.
    if (pathname === ROUTES.mobileCapture && new URLSearchParams(search).get('token')) {
      return undefined;
    }
    // ProtectedRoute sends a phone on any other desktop path to capture.
    if (!isMobileSupportedPath(pathname)) return QuickCapturePage;
  }
  const table = phone ? [...PHONE_PAGES, ...DESKTOP_PAGES] : DESKTOP_PAGES;
  return table.find(([pattern]) => matchPath(pattern, pathname))?.[1];
}

let started = false;

/** Acts on the first call of a page load only: a later sign-in is not a boot. */
export function preloadBootRoute(): void {
  if (started) return;
  started = true;
  pageFor(window.location.pathname, window.location.search, isPhoneViewport())?.preload();
}

/** @internal Test seams. */
export const __bootRoutePreloadForTests = {
  pageFor,
  reset: () => {
    started = false;
  },
};
